// hooks/useConversationListLogic.js
import { useEffect, useState, useRef, useMemo } from "react";
import { useAuthContext } from "../context/AuthContext";
import {
  useEffectivePermissions,
  scopeSetFromPermissions,
} from "./useEffectivePermissions";
import {
  ALL_OWNERSHIP_FILTERS,
  STATUS_FILTERS,
  UNREAD_FILTER,
  conversationMatchesQuickFilters,
} from "../utils/conversationQuickFilters";

export const CHAT_BLOCK_OTHERS_SCOPE = "chat.block_others";
export const CHAT_BLOCK_QUEUE_SCOPE = "chat.block_queue";
export const CHAT_BLOCK_BOTS_SCOPE = "chat.block_bots";
export { STATUS_FILTERS, UNREAD_FILTER, conversationMatchesQuickFilters };
export { conversationMatchesLocalSearch } from "../utils/conversationQuickFilters";

function ownershipFiltersForScopes(scopesSet) {
  return ALL_OWNERSHIP_FILTERS.filter((f) => {
    if (f === "Outros Usuários") return !scopesSet.has(CHAT_BLOCK_OTHERS_SCOPE);
    if (f === "Fila") return !scopesSet.has(CHAT_BLOCK_QUEUE_SCOPE);
    if (f === "ChatBots" || f === "Agentes de IA") {
      return !scopesSet.has(CHAT_BLOCK_BOTS_SCOPE);
    }
    return true;
  });
}

function isScrollContainerOverflowing(container) {
  if (!container) return false;
  return container.scrollHeight > container.clientHeight + 8;
}

export const useConversationListLogic = ({
  conversations,
  selectedConversation,          // (mantido para futuras dependências)
  onSelectConversation,
  refetchConversations,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,                    // (mantido para futuras dependências/UX)
  initialLoading,                // (mantido para futuras dependências/UX)
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
}) => {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [selectedForContextMenu, setSelectedForContextMenu] = useState(null);

  const { userData } = useAuthContext();
  const { data: permData } = useEffectivePermissions();
  const scopesSet = useMemo(
    () => scopeSetFromPermissions(permData, userData),
    [permData, userData]
  );

  const ownershipFilters = useMemo(
    () => ownershipFiltersForScopes(scopesSet),
    [scopesSet]
  );
  const additionalFilters = [UNREAD_FILTER, ...STATUS_FILTERS];

  /** Preset "Meu dia": foco do atendente ao logar */
  const MEU_DIA_FILTERS = ["Minhas Solicitações", "Pendente", "Em Atendimento"];
  const isMeuDiaActive =
    Array.isArray(filters) &&
    filters.length === MEU_DIA_FILTERS.length &&
    MEU_DIA_FILTERS.every((f) => filters.includes(f));
  const setMeuDia = () => setFilters(MEU_DIA_FILTERS);

  const bottomRef = useRef(null);

  const listRef = useRef();

  // Remove filtros de contexto que o cargo não pode mais usar (ex.: filtros salvos).
  useEffect(() => {
    if (!Array.isArray(filters) || !filters.length) return;
    const allowed = new Set(ownershipFilters);
    const next = filters.filter((f) => {
      if (!ALL_OWNERSHIP_FILTERS.includes(f)) return true;
      return allowed.has(f);
    });
    if (next.length !== filters.length) setFilters(next);
  }, [filters, ownershipFilters, setFilters]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchTerm, filters]);


  const filteredConversations = useMemo(() => {
    const list = Array.isArray(conversations) ? conversations : [];
    const activeFilters = Array.isArray(filters) ? filters : [];

    /* ===================== Normalização, filtro e ORDEM ===================== */
    const normalizeTs = (ts) => {
      if (typeof ts === "number") return Number.isFinite(ts) ? ts : 0;
      const d = new Date(ts);
      return Number.isFinite(d.getTime()) ? d.getTime() : 0;
    };
    
    const filtered = list
      .map((c) => ({
        ...c,
        unread: Number(c?.unread ?? 0),
        unreadClient: Number(c?.unreadClient ?? 0),
        _ts: normalizeTs(c?.timestamp),
        _pinned: c?.pinned ? 1 : 0,
      }))
      .filter((c) =>
        conversationMatchesQuickFilters(c, {
          filters: activeFilters,
          searchTerm,
          userName: userData?.nome,
        })
      );

    // 📌 ORDEM: fixadas no topo, depois mais recentes (timestamp desc) — igual WhatsApp
    return filtered
      .sort(
        (a, b) =>
          b._pinned - a._pinned ||
          b._ts - a._ts ||
          (Number(b.id) - Number(a.id))
      )
      .slice();
  }, [conversations, filters, searchTerm, userData]);

  const wantsUnreadFilter = (filters || []).includes(UNREAD_FILTER);
  const unreadPrefetchPagesRef = useRef(0);

  useEffect(() => {
    unreadPrefetchPagesRef.current = 0;
  }, [searchTerm, filters]);

  // Não Lidas filtra no cliente: se a página vier cheia de lidas, busca mais
  // para não deixar a lista vazia/curta sem o usuário conseguir rolar.
  useEffect(() => {
    if (!wantsUnreadFilter) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (typeof fetchNextPage !== "function") return;
    if (filteredConversations.length >= 12) return;
    if (unreadPrefetchPagesRef.current >= 4) return;
    unreadPrefetchPagesRef.current += 1;
    fetchNextPage();
  }, [
    wantsUnreadFilter,
    hasNextPage,
    isFetchingNextPage,
    filteredConversations.length,
    fetchNextPage,
  ]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const el = bottomRef.current;
    if (!el) return;

    const scrollRoot = el.closest(".sidebar-scroll") || listRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        // Lista curta (ex.: busca local com poucos itens): o sentinela fica sempre
        // visível e dispararia fetchNextPage em loop sem esta guarda.
        if (!isScrollContainerOverflowing(scrollRoot)) return;
        fetchNextPage();
      },
      {
        root: scrollRoot,
        threshold: 0.1,
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [filteredConversations.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /* ===================== Handlers básicos ===================== */
  const handleSearchChange = (event) => {
    setSearchTerm(event?.target?.value || "");
  };

  const handleFilterClick = (event) => setFilterAnchor(event.currentTarget);
  const handleFilterClose = () => setFilterAnchor(null);

  const toggleFilter = (filter) => {
    setFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  };

  const sanitizeText = (htmlString) => {
    if (!htmlString) return "—";
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    const text = div.textContent || div.innerText || "";
    return text.trim() || "—";
    // (apenas para preview; render seguro do conteúdo deve ser feito no item)
  };

  /* ===================== Scroll / paginação ===================== */
  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const threshold = el.scrollHeight - el.clientHeight - 50;

    // refresh suave ao "puxar" para cima (opcional)
    if (scrollTop < 0 && typeof refetchConversations === "function") {
      refetchConversations();
    }

    // paginação infinita (carrega próxima página quando chega ao final)
    if (
      isScrollContainerOverflowing(el) &&
      el.scrollTop >= threshold &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  /* ===================== Context menu / popup ===================== */
  const handleOpenInPopup = (conversation) => {
    if (!conversation?.id) return; // guard
    if (String(conversation.id).startsWith("wa-personal:")) return;
    const width = window.screen.width;
    const height = window.screen.height;

    window.open(
      `/popup-call/${conversation.id}`,
      "ConversationPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  const handleContextMenu = (event, conversation) => {
    event.preventDefault();
    setSelectedForContextMenu(conversation || null);
    setContextMenuAnchor({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(null);
    setSelectedForContextMenu(null);
  };

  /* ===================== Click em item ===================== */
  const handleItemClick = (event, conversation) => {
    if (!conversation) return; // guard
    if (event?.ctrlKey) {
      handleOpenInPopup(conversation);
    } else if (typeof onSelectConversation === "function") {
      onSelectConversation(conversation);
    }
  };

  /* ===================== Exports ===================== */
  return {
    listRef,
    bottomRef,
    filterAnchor,
    contextMenuAnchor,
    selectedForContextMenu,
    ownershipFilters,
    additionalFilters,
    isMeuDiaActive,
    setMeuDia,
    handleSearchChange,
    handleFilterClick,
    handleFilterClose,
    toggleFilter,
    sanitizeText,
    handleScroll,
    handleOpenInPopup,
    handleContextMenu,
    handleContextMenuClose,
    handleItemClick,
    filteredConversations,
  };

};
